<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Condition } from "@/modules/rule_engine/ruleEngineClasses";
import { describeCondition } from "@/modules/rule_engine/describe";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import RuleSegments from "@/modules/rule_engine/components/RuleSegments.vue";

const { t } = useI18n();

const emit = defineEmits(["edit-condition", "delete-condition"]);

const props = withDefaults(
	defineProps<{
		conditions: Condition[];
		editIsAllowed?: boolean;
	}>(),
	{ editIsAllowed: false }
);

const store = useRuleEnginedStore();

watch(
	() => props.conditions ?? [],
	(conditions) =>
		conditions.forEach(({ dimension }) => {
			if (dimension?.has_predefined_options) {
				store.getDimensionValues(dimension.uuid);
			}
		}),
	{ immediate: true }
);

const describedConditions = computed(() =>
	(props.conditions ?? []).map((condition) => ({
		condition,
		segments: describeCondition(condition, t, {
			abTests: store.ruleEngineState.abTests,
			dimensionValues: store.dimensionValueCache,
		}),
	}))
);
</script>

<template>
	<div
		v-for="({ condition, segments }, index) in describedConditions"
		:key="condition.uuid ?? index"
		class="d-flex condition py-1"
	>
		<rule-segments
			:connector="index === 0 ? t('rule_engine.if') : t('rule_engine.and')"
			:segments="segments"
		/>
		<v-spacer />
		<div v-if="editIsAllowed" class="flex-shrink-0">
			<v-btn
				@click="emit('edit-condition', condition)"
				variant="text"
				color="#1abc9c"
				>Edit</v-btn
			>
			<v-btn
				@click="emit('delete-condition', condition)"
				variant="text"
				color="error"
				>Delete</v-btn
			>
		</div>
	</div>
</template>

<style scoped lang="scss">
.condition {
	border-left: 5px solid #1abc9c;
	padding-left: 0.75rem;
}
</style>
